/*
 * live-preview-health.js — tells us when the Live Preview stops working for a
 * real customer, and how many of them walk away after touching it.
 *
 * WHY THIS EXISTS, AND WHY IT ASSERTS SUCCESS RATHER THAN CATCHING ERRORS.
 * The two failures that actually reached customers both threw nothing at all:
 * the order's cover-preview link opened an empty shop page, and the descriptor
 * came out as a broken image in the admin because the property name collided
 * with Cloudlift's. A conventional error tracker would have stayed silent
 * through both. So this file mostly checks that the things that MUST be true
 * after a customer types are in fact true — overlay mounted, artwork decoded,
 * descriptor written and parseable, text drawn, real webfont in use — and
 * reports the ones that are not.
 *
 * PURE OBSERVER. It reads the DOM and never calls into <live-preview>, so it
 * cannot break the feature it is watching. Everything is wrapped: a fault in
 * the monitor must never cost a sale.
 *
 * PRIVACY. The customer's text is a personal name. It is NEVER sent — only its
 * length and line count. Same for anything else identifying: nothing here
 * leaves the page except sizes, counts, ids and fault codes.
 *
 * WHERE IT REPORTS, and the split between the two:
 *   - window.dataLayer / window.clarity — EVERYTHING, including the funnel.
 *     This is behaviour, cookie consent governs it (Consentmo), and a customer
 *     who declined analytics reports nothing. So the counts are a floor.
 *   - beaconUrl — FAULTS ONLY, never the funnel. A fault report carries no
 *     personal data and no identifier; it exists so that a broken shop reaches
 *     a human instead of waiting to be noticed in a dashboard. That is error
 *     monitoring, not analytics, and it must not go quiet precisely when the
 *     customer declined analytics — their broken sale counts the same.
 *
 * The beacon accepts any endpoint, and understands one shape specially: paste a
 * Google Form's PRE-FILLED link and it posts into that form, whose responses
 * land in a Sheet somebody can actually read. One setting, no backend, no
 * account beyond one the shop already has.
 */
(function () {
  'use strict';

  // Audit at these delays after the customer first engages. The first catches a
  // hard failure while they are still on the page; the second lets a slow font
  // or cover land before we accuse it of anything.
  var AUDIT_DELAYS = [2500, 8000];
  var MSG_MAX = 200;

  // Severity, for US. NOT a wall for the customer — see setNotice().
  //
  // The original design held add-to-cart on any of these, on the reasoning that
  // it is better to lose a sale than to take an order nobody can print. That
  // premise turned out to be false: the personalisation textarea is an ordinary
  // form field (`name="properties[Κείμενο]"`, `form=<product form>`), so the
  // customer's words reach the order by the browser's own doing, even if every
  // line of this feature's JavaScript fails. And the geometry, font and artwork
  // are ours — `tools/live-preview/product-configs/*.json` plus
  // `covers.production.json` — so a descriptor can be rebuilt from the text, the
  // product and the variant. There is no such thing here as an order nobody can
  // print; there are only orders that need a rebuild.
  //
  // So a fault is now a thing to REPORT and to warn about, never a reason to
  // take the button away from someone holding a card. `font-fallback` stays out
  // of this list because it is not even worth mentioning to the customer, and
  // `overlay-hidden` because it is not a fault at all — see slideHidden().
  var FATAL = {
    'overlay-missing': 1, 'overlay-collapsed': 1,
    'artwork-not-found': 1, 'artwork-not-loaded': 1, 'artwork-mismatch': 1,
    'descriptor-empty': 1, 'descriptor-unparseable': 1, 'descriptor-no-layers': 1,
    'descriptor-no-content': 1, 'descriptor-no-base': 1,
    'text-not-drawn': 1, 'text-not-sized': 1,
  };

  function anyFatal(faults) {
    for (var i = 0; i < faults.length; i++) if (FATAL[faults[i]]) return true;
    return false;
  }

  // Set once from the block config; used by the page-level error handler too.
  var BEACON = null;

  // Build the sender for whatever endpoint was configured.
  //
  // A Google Form is the whole backend. The merchant pastes its PRE-FILLED link
  // — the one the form itself hands out — and the field id is already sitting in
  // that URL, so there is a single setting and nothing to look up by hand. We
  // rewrite /viewform to /formResponse and post the payload into that one field.
  // sendBeacon is fire-and-forget and opaque, which suits a form that answers
  // with a redirect we are not allowed to read anyway.
  function makeSender(url) {
    if (!url || !navigator.sendBeacon) return null;

    if (url.indexOf('docs.google.com/forms') === -1) {
      return function (payload) {
        navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
      };
    }

    try {
      var u = new URL(url);
      var entry = null;
      u.searchParams.forEach(function (v, k) {
        if (!entry && k.indexOf('entry.') === 0) entry = k;
      });
      if (!entry) {
        // The share link (…?usp=publish-editor) carries no field id, only the
        // PRE-FILLED one does — an easy and invisible mistake to make, since a
        // beacon that goes nowhere looks exactly like a shop with no faults.
        // Say so out loud rather than letting this file fail the way it exists
        // to prevent. Setup-time only: nothing reaches here without a form URL.
        try {
          console.warn(
            '[live-preview] Health beacon URL is a Google Form link with no entry.N field, ' +
            'so nothing will be reported. Use the form\u2019s PRE-FILLED link instead ' +
            '(\u22ee \u2192 Get pre-filled link), which carries the field id in the URL.'
          );
        } catch (e) { /* no console, no problem */ }
        return null;
      }
      var post = u.origin + u.pathname.replace(/\/viewform$/, '/formResponse');
      return function (payload) {
        var body = new URLSearchParams();
        body.set(entry, JSON.stringify(payload));
        navigator.sendBeacon(
          post,
          new Blob([body.toString()], { type: 'application/x-www-form-urlencoded;charset=UTF-8' })
        );
      };
    } catch (e) {
      return null;
    }
  }

  // Everything the beacon is allowed to carry. No text, no identifier, no UA.
  function beaconPayload(extra) {
    var out = { at: new Date().toISOString(), shop: location.host, path: location.pathname };
    for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) out[k] = extra[k];
    return out;
  }

  function clip(s) {
    s = String(s == null ? '' : s);
    return s.length > MSG_MAX ? s.slice(0, MSG_MAX) + '…' : s;
  }

  function fileOf(url) {
    try { return String(url).split('/').pop().split('?')[0]; } catch (e) { return ''; }
  }

  function parseConfig(el) {
    try { return JSON.parse(el.dataset.config || '{}'); } catch (e) { return {}; }
  }

  // Is the overlay's slide simply not the one on show? Dawn's desktop thumbnail
  // layouts display one media item and `display:none` the others, so a customer
  // who clicked to an inside page after typing has a collapsed box — and a
  // preview that is fine, one thumbnail away. That is browsing, not a
  // breakage, and it must not raise a notice or ask live-preview.js to
  // repair anything: its repair would flip the gallery back to the cover under
  // the customer's cursor. Only when ANOTHER item is visibly active; a gallery
  // hidden as a whole is still a fault.
  function slideHidden(overlay) {
    var li = overlay.closest && overlay.closest('.product__media-item');
    if (!li || li.classList.contains('is-active') || !li.parentElement) return false;
    var active = li.parentElement.querySelector('.product__media-item.is-active');
    return !!(active && active !== li && active.getBoundingClientRect().height > 2);
  }

  // Same lookup live-preview.js does, so we judge the element it actually drew on.
  function heroImage(el, cfg) {
    var root = document.querySelector(cfg.heroSelector || 'media-gallery');
    var img = root && (root.querySelector('.product__media img') || root.querySelector('img'));
    return img || el.querySelector('[data-lp-base]');
  }

  // document.fonts.check() is NOT usable here: it answers "can this be rendered",
  // and returns TRUE for a family that was never declared, because a fallback can
  // always render it. That is exactly the failure we care about — the fit runs
  // against the fallback face and the text comes out at the wrong size, with no
  // error anywhere. Ask the face registry instead.
  function faceLoaded(family) {
    if (!family || !document.fonts || !document.fonts.forEach) return true; // can't tell — don't cry wolf
    var found = false;
    try {
      document.fonts.forEach(function (f) {
        if (f.family === family && f.status === 'loaded') found = true;
      });
    } catch (e) {
      return true;
    }
    return found;
  }

  // What must be true once a customer has typed. Returns fault codes, [] = healthy.
  // `late` gates the checks that are only meaningful once the cover swap has had
  // time to settle — asserting them mid-flight would invent faults.
  function audit(el, late) {
    var faults = [];
    var cfg = parseConfig(el);
    var input = el.querySelector('[data-lp-input]');
    var descInput = el.querySelector('[data-lp-preview]');
    var text = input ? input.value : '';

    // The overlay is appended INTO the gallery, outside this element.
    var overlay = document.querySelector('.lp-overlay');
    var textEl = overlay && overlay.querySelector('.lp-overlay-text');

    // A page nobody touched is not a broken page.
    //
    // The overlay is created LAZILY, on the customer's first interaction — that
    // is deliberate, so a cold PDP keeps the native gallery as its LCP element.
    // So "no overlay and no text" does not mean the preview failed; it means the
    // customer is buying the book without personalising it, which is an ordinary
    // sale and most of this catalogue's sales on some products.
    //
    // Auditing it anyway raised `overlay-missing` on EVERY such add-to-cart:
    // the customer was shown "η προεπισκόπηση δεν φόρτωσε σωστά" and had the
    // button taken away at the moment of buying, and a fault was reported for a
    // shop that was working perfectly. Order #7674 on 2026-08-21 is the case
    // that surfaced it — bought without personalisation, seven minutes after the
    // beacon fired. Reproduced live before this line was written.
    if (!overlay && !text.trim()) return faults;

    var boxVisible = false;
    if (!overlay || !overlay.isConnected) {
      faults.push('overlay-missing');
    } else {
      var r = overlay.getBoundingClientRect();
      // Dawn has flattened this box before (a `.media > :not(...)` rule at 0,3,0).
      if (r.width < 2 || r.height < 2) faults.push(slideHidden(overlay) ? 'overlay-hidden' : 'overlay-collapsed');
      else boxVisible = true;
    }

    var img = heroImage(el, cfg);
    if (!img) faults.push('artwork-not-found');
    else if (!img.naturalWidth) faults.push('artwork-not-loaded');

    // Everything below only means something once there IS text to render.
    var descBase = null;
    if (text.trim()) {
      var raw = descInput ? descInput.value : '';
      if (!raw) {
        // The sale-losing one: they typed, and nothing will reach the order.
        faults.push('descriptor-empty');
      } else {
        try {
          var d = JSON.parse(raw);
          if (!d.layers || !d.layers[0]) faults.push('descriptor-no-layers');
          else if (!d.layers[0].content) faults.push('descriptor-no-content');
          if (!d.base) faults.push('descriptor-no-base');
          else descBase = d.base;
        } catch (e) {
          faults.push('descriptor-unparseable');
        }
      }

      if (textEl) {
        if (!textEl.textContent.trim()) faults.push('text-not-drawn');
        // A fitted size is never below the product's own floor (fitText() in
        // live-preview.js stops there), so «unusable» is a text the fit never
        // touched while its box was on screen — no inline size at all — or one
        // that came out at nothing. The old test, `< 6px`, was true of every
        // long dedication on a phone: 15 lines in a 90px box are 4.5px and
        // fitted exactly as asked. Those were the school- and travel-planner
        // reports, and they were not breakages.
        var px = parseFloat(getComputedStyle(textEl).fontSize) || 0;
        var fitted = !!(textEl.style && textEl.style.fontSize);
        if (boxVisible && (!fitted || px < 1)) faults.push('text-not-sized');
      }

      var fam = cfg.font && cfg.font.family;
      if (fam && !faceLoaded(fam)) faults.push('font-fallback');

      // The quietest failure of the lot. If the personalised cover fails to load,
      // swapHero never paints it and the hero keeps showing the GALLERY photo —
      // so the customer's name sits on generic artwork, the image is perfectly
      // healthy, nothing throws, and every check above passes. Only comparing
      // what is on screen against what the descriptor claims catches it.
      if (late && img && descBase) {
        var shown = img.currentSrc || img.getAttribute('src') || '';
        if (fileOf(shown) && fileOf(descBase) && fileOf(shown) !== fileOf(descBase)) {
          faults.push('artwork-mismatch');
        }
      }
    }

    return faults;
  }

  function watch(el) {
    var cfg = parseConfig(el);
    var input = el.querySelector('[data-lp-input]');
    if (!input) return;

    var state = {
      engaged: false,
      submitted: false,
      typed: 0,      // keystroke events, not content
      fontChanges: 0,
      sizeSteps: 0,
      alignChanges: 0,
      reported: {},  // fault code -> true, so one fault is reported once
      repaired: false, // the one lp:recover attempt, spent at most once
      recheck: null,   // interval, alive only while a notice is on screen
      worst: [],
    };

    function facts(extra) {
      var text = input.value || '';
      var out = {
        lp_product: cfg.product || null,
        lp_variant: cfg.variantId || null,
        lp_font: (cfg.font && cfg.font.family) || null,
        lp_text_length: text.length,             // length only — never the text
        lp_text_lines: text ? text.split('\n').length : 0,
        lp_typed: state.typed,
        lp_font_changes: state.fontChanges,
        lp_size_steps: state.sizeSteps,
        // Which kind of picture the order is carrying: 'dom' if the browser
        // drew it and it can therefore contradict the descriptor, 'desc' if it
        // was drawn FROM the descriptor and agreeing with it proves nothing,
        // 'none' if there is no picture at all. Three letters, no text, no
        // length — the personal-name rule is untouched.
        //
        // It exists because the witness can stop being a witness without
        // anything breaking: a CDN that stops answering, a browser that drops
        // foreignObject, a font that 404s after a theme edit. Every one of those
        // degrades silently and correctly, and the shop would go on believing it
        // had a witness. This is the line that says otherwise inside a day.
        lp_capture: (function () {
          try {
            var im = el.querySelector('[data-lp-image]');
            var v = im && im.value;
            if (!v) return 'none';
            var m = /[?&]m=([a-z]+)/.exec(v);
            return m ? m[1] : 'none';
          } catch (e) { return null; }
        })(),
        lp_align_changes: state.alignChanges,
      };
      for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) out[k] = extra[k];
      return out;
    }

    function report(name, data) {
      var row = { event: 'lp_' + name };
      for (var k in data) if (Object.prototype.hasOwnProperty.call(data, k)) row[k] = data[k];

      // gtag FIRST, and this is not belt-and-braces — it is the only half that
      // arrives.
      //
      // `dataLayer.push` alone reaches GA4 only through a GTM container that
      // forwards it, and this shop has none: `gcm-integration-script.liquid`
      // defines gtag as the consent-mode wrapper and Shopify's own tag reads
      // gtag, not arbitrary dataLayer rows. Measured 2026-08-23 — GA4 held 14
      // event names for the five days since the cutover and NOT ONE was `lp_*`,
      // while the sticky bar's two (`sticky_button_view` 166,
      // `sticky_button_click` 14) were there, because its trackEvent() calls
      // gtag as well as pushing. Every engaged / submitted / abandoned this file
      // has ever recorded went nowhere.
      //
      // Exactly the failure this file was written to prevent, turned on itself:
      // the sending half was green the whole time and said nothing about whether
      // anything was listening. Same as `selfTest()` with no form trigger, same
      // as `document.fonts.check()`. When something looks healthy, ask what it
      // actually proves.
      //
      // The FAULT beacon was never affected — it posts to the Form directly and
      // is verified end to end. What was lost is the funnel: engagement and
      // abandonment, which is also why it stayed invisible. Nobody misses
      // numbers they have never seen.
      try {
        if (typeof window.gtag === 'function') {
          var params = {};
          for (var g in row) {
            if (g !== 'event' && Object.prototype.hasOwnProperty.call(row, g) && row[g] != null) {
              params[g] = row[g];
            }
          }
          window.gtag('event', 'lp_' + name, params);
        }
      } catch (e) { /* analytics must never break the page */ }

      // Kept for a GTM container if one is ever added, and harmless without one.
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(row);
      } catch (e) { /* analytics must never break the page */ }

      try {
        if (typeof window.clarity === 'function') {
          // Tags make the session findable and REPLAYABLE in Clarity, which is
          // the closest thing to watching the customer it happened to.
          if (data.lp_faults) window.clarity('set', 'lp_fault', String(data.lp_faults));
          if (name === 'error') window.clarity('set', 'lp_error', String(data.lp_code || '1'));
          window.clarity('event', 'lp_' + name);
        }
      } catch (e) { /* ditto */ }

      // Faults only — the funnel stays in GA4/Clarity where consent governs it.
      try {
        if (BEACON && name === 'broken' && data.lp_fatal) {
          BEACON(beaconPayload({
            kind: 'broken',
            faults: data.lp_faults,
            when: data.lp_when,
            product: data.lp_product,
            variant: data.lp_variant,
            font: data.lp_font,
          }));
        }
      } catch (e) { /* ditto */ }
    }

    function settle(faults, when, late) {
      state.worst = faults;
      // Tell the customer before telling us — they are the one standing there.
      // The FIRST audit never speaks: at 2.5s a slow hero or an unsettled layout
      // looks exactly like a breakage, and saying so to someone who is mid-flow
      // creates the problem it is describing. Only the late audit and the moment
      // of adding to cart are allowed to show anything.
      var mayShow = when !== 't+' + AUDIT_DELAYS[0];
      var show = mayShow && anyFatal(faults);
      try { setNotice(el, cfg, show); } catch (e) { /* never break the page */ }
      // A message that outlives the problem is its own problem. Audits only run
      // at fixed moments, so once the notice is up nothing was looking any more:
      // the customer could fix it — rotate, scroll, let the cover finish
      // loading — and still be told the preview is broken while it sits there
      // working. Watch, cheaply, only while the notice is showing, and take it
      // down the moment it stops being true.
      if (show) watchForRecovery(el, cfg, late);
      // `overlay-hidden` is the customer looking at another photo. It is not
      // reported anywhere: a count of it would read as breakage in GA4.
      var fresh = faults.filter(function (f) { return !state.reported[f] && f !== 'overlay-hidden'; });
      if (!fresh.length) return;
      fresh.forEach(function (f) { state.reported[f] = true; });
      report('broken', facts({ lp_faults: fresh.join(','), lp_when: when, lp_fatal: anyFatal(fresh) }));
    }

    function watchForRecovery(el, cfg, late) {
      if (state.recheck) return;
      state.recheck = setInterval(function () {
        var f;
        try { f = audit(el, late); } catch (e) { return; }
        if (anyFatal(f)) return;
        clearInterval(state.recheck);
        state.recheck = null;
        try { setNotice(el, cfg, false); } catch (e) { /* never break the page */ }
      }, 3000);
    }

    function runAudit(when, late) {
      var faults;
      try { faults = audit(el, late); } catch (e) { return; }
      // Try to fix it before mentioning it. Once per session: if the repair
      // works the customer never learns anything was wrong, and if it does not,
      // we are exactly where we would have been.
      if (faults.length && anyFatal(faults) && !state.repaired) {
        state.repaired = true;
        try { el.dispatchEvent(new CustomEvent('lp:recover')); } catch (e) { /* older browser */ }
        setTimeout(function () {
          var after;
          try { after = audit(el, late); } catch (e) { return; }
          settle(after, when, late);
        }, 350);
        return;
      }
      settle(faults, when, late);
    }

    function engage() {
      if (state.engaged) return;
      state.engaged = true;
      report('engaged', facts({}));
      AUDIT_DELAYS.forEach(function (ms, i) {
        setTimeout(function () { runAudit('t+' + ms, i > 0); }, ms);
      });
    }

    input.addEventListener('focus', engage, { once: true });
    input.addEventListener('input', function () { state.typed++; engage(); });
    el.querySelectorAll('[data-lp-align-btn]').forEach(function (b) {
      b.addEventListener('click', function () { state.alignChanges++; engage(); });
    });
    ['[data-lp-size-dec]', '[data-lp-size-inc]'].forEach(function (sel) {
      var b = el.querySelector(sel);
      if (b) b.addEventListener('click', function () { state.sizeSteps++; engage(); });
    });
    var fontSel = el.querySelector('[data-lp-font]');
    if (fontSel) fontSel.addEventListener('change', function () { state.fontChanges++; engage(); });

    // Add-to-cart is the moment that matters most: a descriptor that is broken
    // HERE becomes an order we cannot print, which is worse than a lost sale.
    var descInput = el.querySelector('[data-lp-preview]');
    var form = descInput && descInput.getAttribute('form')
      ? document.getElementById(descInput.getAttribute('form'))
      : el.closest('form');
    if (form) {
      form.addEventListener('submit', function () {
        state.submitted = true;
        runAudit('submit', true);
        report('submitted', facts({ lp_faults: state.worst.join(',') || null }));
        // Were the line breaks recorded, and if not, which gate refused? As an
        // EVENT NAME with a fixed vocabulary — GA4 counts event names without
        // any admin-side registration, which the parameters above never got.
        // Reads the descriptor's own `linesWhy`; carries no text.
        try {
          var raw = descInput && descInput.value;
          var d = raw ? JSON.parse(raw) : null;
          var layer = d && d.layers && d.layers[0];
          if (layer) {
            var why = Array.isArray(layer.lines) ? 'ok' : String(layer.linesWhy || 'unknown');
            if (!/^[a-z]{1,12}$/.test(why)) why = 'unknown';
            if (typeof window.gtag === 'function') window.gtag('event', 'lp_lines_' + why, {});
            if (typeof window.clarity === 'function') window.clarity('event', 'lp_lines_' + why);
          }
        } catch (e) { /* analytics must never break the page */ }
      });
    }

    // Hit and run: engaged with the personalisation, then left without adding to
    // cart. pagehide fires where unload does not (bfcache, mobile Safari).
    window.addEventListener('pagehide', function () {
      if (state.recheck) { clearInterval(state.recheck); state.recheck = null; }
      if (!state.engaged || state.submitted) return;
      report('abandoned', facts({ lp_faults: state.worst.join(',') || null }));
    });
  }

  // Say so, and hold add-to-cart. A customer who cannot see their cover is one
  // click away from an order we cannot fulfil, and refunding that costs more
  // than the sale — so a broken preview asks them to come back rather than
  // letting a wrong order through. Reversible: if a later audit comes back
  // clean, the notice goes and the button returns.
  // Tell the customer, and get out of their way.
  //
  // This used to disable add-to-cart. It no longer does, and the reason is in
  // the comment on FATAL: their text reaches the order regardless, so blocking
  // buys nothing and costs the sale — and a customer who is stopped at the
  // moment of paying does not come back. `role="status"` rather than `alert`,
  // because this is advice, not an emergency.
  //
  // It still RE-ENABLES a button an older version of this file disabled, so a
  // page left open across the deploy recovers on its next audit.
  function setNotice(el, cfg, on) {
    var box = el.querySelector('[data-lp-alert]');
    if (!box) {
      if (!on) return;
      box = document.createElement('p');
      box.className = 'lp-alert';
      box.setAttribute('data-lp-alert', '');
      box.setAttribute('role', 'status');
      box.textContent = cfg.faultMessage ||
        'Η προεπισκόπηση δεν εμφανίζεται σωστά αυτή τη στιγμή. Το κείμενό σας ' +
        'καταγράφεται κανονικά και μπορείτε να συνεχίσετε — το εξώφυλλο το ' +
        'ελέγχουμε εμείς πριν τυπωθεί.';
      el.insertBefore(box, el.firstChild);
    }
    box.hidden = !on;

    var descInput = el.querySelector('[data-lp-preview]');
    var formId = descInput && descInput.getAttribute('form');
    var form = formId ? document.getElementById(formId) : el.closest('form');
    var btn = form && form.querySelector('[name="add"]');
    if (btn && btn.hasAttribute('data-lp-halted')) {
      btn.disabled = false;
      btn.removeAttribute('data-lp-halted');
    }
  }

  // Uncaught exceptions thrown by our own files. Filtered by filename so we do
  // not report every third-party script on the product page as ours.
  function watchErrors() {
    window.addEventListener('error', function (e) {
      var f = fileOf(e.filename || '');
      if (f.indexOf('live-preview') !== 0) return;
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'lp_error', lp_code: 'js-error', lp_message: clip(e.message),
          lp_file: f, lp_line: e.lineno || null,
        });
        if (typeof window.clarity === 'function') {
          window.clarity('set', 'lp_error', f + ':' + (e.lineno || '?'));
          window.clarity('event', 'lp_error');
        }
        // An exception thrown by our own code is a fault by definition.
        if (BEACON) BEACON(beaconPayload({ kind: 'js-error', message: clip(e.message), file: f, line: e.lineno || null }));
      } catch (err) { /* never break the page */ }
    });

    window.addEventListener('unhandledrejection', function (e) {
      var stack = '';
      try { stack = String((e.reason && e.reason.stack) || e.reason || ''); } catch (err) { return; }
      if (stack.indexOf('live-preview') === -1) return;
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'lp_error', lp_code: 'unhandled-rejection', lp_message: clip(stack) });
        if (typeof window.clarity === 'function') window.clarity('event', 'lp_error');
      } catch (err) { /* never break the page */ }
    });
  }

  // ⛔ A STANDALONE preview is not this monitor's business (2026-09-24).
  //
  // The companion card (snippets/live-preview-companion.liquid) puts a second
  // <live-preview> on the page — inside the cart drawer, which the layout
  // renders BEFORE <main>. So on any page with an eligible book in the cart it
  // was element [0], and two things followed, both measured: its config has no
  // `beaconUrl`, so `makeSender(null)` returned null and NO fault was reported
  // for that page view at all; and `audit()` resolved the hero through
  // `document.querySelector('media-gallery')` — the OTHER product's — and the
  // overlay through a page-wide `.lp-overlay`, now the card's, so it filed a
  // FATAL `artwork-mismatch` and told the customer «Η προεπισκόπηση δεν
  // εμφανίζεται σωστά» while the cover was drawing perfectly.
  //
  // This monitor exists for the product page's own preview. The card's
  // correctness is asserted elsewhere (tools/live-preview/harness/
  // suite_companion.js), and the order it writes is checked by the print
  // pipeline like any other.
  function ownPreviews() {
    var out = [];
    var all = document.querySelectorAll('live-preview');
    for (var i = 0; i < all.length; i++) {
      var cfg = null;
      try { cfg = parseConfig(all[i]); } catch (e) { cfg = null; }
      if (cfg && cfg.standalone) continue;
      out.push(all[i]);
    }
    return out;
  }

  function start() {
    var els = ownPreviews();
    if (!els.length) return; // not a personalised product page
    try { BEACON = makeSender(parseConfig(els[0]).beaconUrl); } catch (e) { BEACON = null; }
    watchErrors();
    els.forEach(function (el) {
      try { watch(el); } catch (e) { /* a broken monitor must cost nothing */ }
    });
  }

  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();
